import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  CounterpartiesExpenseQueryModel,
  CounterpartyDataGrid,
  CounterpartyDataTotals,
  CreateExpensePayload,
  GenerateExpensePayload,
  MeteringPointDataGrid,
  MeteringPointExpense,
  MeteringPointFilters,
  MeteringPointQuery,
  OperatingExpensesMeteringPointInfo,
  UpdateExpensePayload,
} from './types';

export const operationalExpenseApi = createApi({
  reducerPath: 'operationalExpenseApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/counterparty-operational-expenses`,
  }),
  tagTypes: [
    'OperatingExpenses',
    'OperatingExpense',
    'OperatingExpenseMeteringPoints',
    'OperatingExpenseCounterparties',
    'OperatingExpensesMeteringPointInfo',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getOperatingExpensesI: builder.infiniteQuery<
      CounterpartyDataGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: '/counterparties/query',
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['OperatingExpenses', 'OperatingExpense'],
    }),
    getOperatingExpensesMeteringPointsI: builder.infiniteQuery<
      MeteringPointDataGrid[],
      (FiltersGrid & MeteringPointFilters) | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: '/metering-points/query',
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['OperatingExpenseMeteringPoints'],
    }),
    getOperatingExpensesMeteringPointsTotals: builder.query<
      CounterpartyDataTotals,
      (FiltersGrid & MeteringPointFilters) | void
    >({
      query: params => {
        return {
          url: '/metering-points/query/totals',
          params,
        };
      },
      providesTags: () => ['OperatingExpenseMeteringPoints'],
    }),
    getOperatingExpensesTotals: builder.query<
      CounterpartyDataTotals,
      (FiltersGrid & { date?: string }) | void
    >({
      query: params => {
        return {
          url: '/counterparties/query/totals',
          params,
        };
      },
      providesTags: () => ['OperatingExpenses'],
    }),
    getOperatingExpensesMeteringPointInfo: builder.query<
      OperatingExpensesMeteringPointInfo,
      MeteringPointQuery
    >({
      query: params => ({
        url: '/metering-points/info',
        params,
      }),
      providesTags: () => ['OperatingExpensesMeteringPointInfo'],
    }),
    getMeteringPointExpenses: builder.query<MeteringPointExpense, string>({
      query: id => ({
        url: `/metering-points/expense/${id}`,
      }),
    }),
    createDailyExpense: builder.mutation<
      MeteringPointExpense,
      CreateExpensePayload
    >({
      query: data => ({
        url: '/metering-points/expense',
        method: 'POST',
        data,
      }),
      invalidatesTags: (_, __, payload) => [
        'OperatingExpenseCounterparties',
        'OperatingExpenseMeteringPoints',
        'OperatingExpenses',
        {
          type: 'OperatingExpense',
          id: `${payload.counterpartyId}-${payload.contractId}-${payload.structuralSubdivisionId}`,
        },
      ],
    }),
    generateExpense: builder.mutation<
      MeteringPointExpense,
      GenerateExpensePayload
    >({
      query: data => ({
        url: '/metering-points/expense/generate',
        method: 'POST',
        data,
      }),
    }),
    updateDailyExpense: builder.mutation<
      MeteringPointExpense,
      UpdateExpensePayload
    >({
      query: data => ({
        url: `/metering-points/expense/${data.id}`,
        method: 'PUT',
        data: {
          id: data.id,
          dailyExpenses: data.dailyExpenses,
          ignoreUnauthorizedConsumption: data.ignoreUnauthorizedConsumption,
        },
      }),
      invalidatesTags: (_, __, payload) => [
        'OperatingExpenseCounterparties',
        'OperatingExpenseMeteringPoints',
        'OperatingExpenses',
        {
          type: 'OperatingExpense',
          id: `${payload.counterpartyId}-${payload.contractId}-${payload.structuralSubdivisionId}`,
        },
      ],
    }),
    fillByCopyInProgress: builder.query<boolean, void>({
      query: () => ({
        url: `/counterparties/fill-by-copy/in-progress`,
      }),
    }),
    fillByCopy: builder.mutation<any, CounterpartiesExpenseQueryModel>({
      query: data => ({
        url: `/counterparties/fill-by-copy`,
        method: 'POST',
        data,
      }),
      invalidatesTags: () => ['OperatingExpenses'],
    }),
    fillByCopyMeteringPoint: builder.mutation<any, GenerateExpensePayload>({
      query: data => ({
        url: `/metering-points/expense/fill-by-copy`,
        method: 'POST',
        data,
      }),
      invalidatesTags: () => [
        'OperatingExpenses',
        'OperatingExpenseMeteringPoints',
      ],
    }),
  }),
});

export const {
  useGetOperatingExpensesTotalsQuery,
  useGetOperatingExpensesMeteringPointsTotalsQuery,
  useGenerateExpenseMutation,
  useLazyGetMeteringPointExpensesQuery,
  useCreateDailyExpenseMutation,
  useUpdateDailyExpenseMutation,
  useGetOperatingExpensesMeteringPointInfoQuery,
  useLazyFillByCopyInProgressQuery,
  useFillByCopyMutation,
  useFillByCopyMeteringPointMutation,
} = operationalExpenseApi;

export const useGetOperatingExpensesIInfiniteQuery = normalizeInfiniteQueryData(
  operationalExpenseApi.useGetOperatingExpensesIInfiniteQuery,
);

export const useGetOperatingExpensesMeteringPointsIInfiniteQuery =
  normalizeInfiniteQueryData(
    operationalExpenseApi.useGetOperatingExpensesMeteringPointsIInfiniteQuery,
  );

const getCachedOperatingExpenseByIdSelector = (counterpartyId, contractId) =>
  cachedItemByIdSelector(
    'operationalExpenseApi',
    'getOperatingExpensesI',
    i => i.counterpartyId === counterpartyId && i.contractId === contractId,
  )('');

export const useGetCachedOperatingExpenseByIdSelector = ({
  counterpartyId,
  contractId,
}) =>
  useSelector(
    getCachedOperatingExpenseByIdSelector(counterpartyId, contractId),
  );
